import dayjs from 'dayjs'
import { DayjsFormats } from './dayjs'

export type RemainingTime = {
  days: number
  hours: number
  minutes: number
  seconds: number
  totalMilliseconds: number
  isExpired: boolean
}

export function calculateRemainingTime(
  createdAt: string | Date,
  durationHours: number
): RemainingTime {
  const createdDate = dayjs.utc(createdAt)

  if (!createdDate.isValid()) {
    throw new Error('Invalid created_at date format')
  }

  const endDate = createdDate.add(durationHours, 'hours')

  const now = dayjs.utc()

  const diffMs = endDate.diff(now)

  if (diffMs <= 0) {
    return {
      days: 0,
      hours: 0,
      minutes: 0,
      seconds: 0,
      totalMilliseconds: 0,
      isExpired: true,
    }
  }

  const duration = dayjs.duration(diffMs)
  const days = Math.floor(duration.asDays())
  const hours = duration.hours()
  const minutes = duration.minutes()
  const seconds = duration.seconds()

  return {
    days,
    hours,
    minutes,
    seconds,
    totalMilliseconds: diffMs,
    isExpired: false,
  }
}

export type FormatOptions = {
  hideSecondsWhenDays?: boolean
  showZero?: boolean
  expiredText?: string
  fullText?: boolean
}

export function formatRemainingTime(
  remainingTime: RemainingTime | null,
  options: FormatOptions = {}
): string {
  const {
    hideSecondsWhenDays = true,
    showZero = false,
    expiredText = 'Expired',
    fullText = false,
  } = options

  if (!remainingTime || remainingTime.isExpired) {
    return expiredText
  }

  const { days, hours, minutes, seconds } = remainingTime

  const parts: string[] = []

  const units = fullText
    ? { d: 'day', h: 'hour', m: 'minute', s: 'second' }
    : { d: 'd', h: 'h', m: 'm', s: 's' }

  const pluralize = (value: number, unit: string) => {
    if (!fullText) return `${value}${unit}`
    return `${value} ${unit}${value !== 1 ? 's' : ''}`
  }

  if (days > 0 || (showZero && hours === 0 && minutes === 0 && seconds === 0)) {
    parts.push(pluralize(days, units.d))
  }
  if (hours > 0 || (showZero && days === 0 && minutes === 0 && seconds === 0)) {
    parts.push(pluralize(hours, units.h))
  }
  if (minutes > 0 || (showZero && days === 0 && hours === 0 && seconds === 0)) {
    parts.push(pluralize(minutes, units.m))
  }
  if (seconds > 0 && (!hideSecondsWhenDays || days === 0)) {
    parts.push(pluralize(seconds, units.s))
  }

  return parts.length > 0 ? parts.join(' ') : pluralize(0, units.s)
}

export function getFormattedRemainingTime(
  createdAt: string | Date,
  durationHours: number,
  options?: FormatOptions
): string {
  try {
    const remaining = calculateRemainingTime(createdAt, durationHours)
    return formatRemainingTime(remaining, options)
  } catch (error) {
    console.error('Error calculating remaining time:', error)
    return options?.expiredText || 'Expired'
  }
}

export function calculateEndDate(createdAt: string | Date, durationHours: number) {
  const createdDate = dayjs.utc(createdAt)

  if (!createdDate.isValid()) {
    throw new Error('Invalid created_at date format')
  }

  return createdDate.add(durationHours, 'hours')
}

/**
 * Gets formatted end date for grace period notifications
 * @param createdAt - creation time
 * @param durationHours - duration in hours
 * @param format - dayjs format string (defaults to "MMM D, YYYY [at] h:mm A")
 * @returns formatted end date string
 */
export function getFormattedEndDate(
  createdAt: string | Date,
  durationHours: number,
  format: string = DayjsFormats.fullWithAt
): string {
  try {
    const endDate = calculateEndDate(createdAt, durationHours)
    return endDate.local().format(format)
  } catch (error) {
    console.error('Error calculating end date:', error)
    return 'Invalid date'
  }
}

/**
 * Checks if the grace period has expired
 * @param createdAt - creation time
 * @param durationHours - duration in hours
 * @returns true if expired, false otherwise
 */
export function isGracePeriodExpired(createdAt: string | Date, durationHours: number): boolean {
  try {
    const endDate = calculateEndDate(createdAt, durationHours)
    const now = dayjs.utc()
    return now.isAfter(endDate)
  } catch (error) {
    console.error('Error checking grace period expiration:', error)
    return true
  }
}
